import React, { useState } from 'react'
import {
  XMarkIcon,
  EyeIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  IdentificationIcon,
  MapPinIcon,
  BriefcaseIcon,
  HomeIcon,
  GlobeAltIcon,
  DocumentIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { Card } from '@/shared/ui/Card'
import { ModerationStatus } from '@/shared/ui/ModerationStatus'
import { ModerationImageViewer } from '@/shared/ui/ModerationImageViewer'
import { ModerationTransportTypes } from '@/shared/ui/ModerationTransportTypes'
import { ModerationSection } from '@/shared/ui/ModerationSection'
import { FIELD_OPTIONS } from '@/shared/ui/RejectModal'
import type { UserDetailsResponse, ModerationHistoryItem } from '@/shared/api'

const ACTION_LABELS: Record<string, string> = {
  created: 'Заявка создана',
  approved: 'Одобрена',
  rejected: 'Отклонена',
  reopened: 'Возвращена на модерацию',
}

const STATUS_LABELS: Record<number, string> = {
  0: 'На модерации', 1: 'Одобрена', 2: 'Отклонена',
}

const fmt = (ts: number) => new Date(ts * 1000).toLocaleString('ru-RU')

interface ImageViewerProps {
  src: string | undefined
  alt: string
  placeholder: string
}

const ImageViewer: React.FC<ImageViewerProps> = ({ src, alt, placeholder }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [imageError, setImageError] = useState(false)

  if (!src || imageError) {
    return (
      <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center">
        <div className="text-center">
          <UserIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500">{placeholder}</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <div
        className="relative cursor-pointer group"
        onClick={() => setIsExpanded(true)}
      >
        <img
          src={src}
          alt={alt}
          className="w-full h-32 object-cover rounded-lg transition-transform group-hover:scale-105"
          onError={() => setImageError(true)}
        />
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200 flex items-center justify-center">
          <EyeIcon className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>

      {/* Expanded Image Modal */}
      {isExpanded && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setIsExpanded(false)}
        >
          <div className="relative max-w-4xl max-h-full">
            <img
              src={src}
              alt={alt}
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setIsExpanded(false)}
              className="absolute top-4 right-4 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-full transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}
    </>
  )
}

interface CourierDetailContentProps {
  courier: UserDetailsResponse
  history?: ModerationHistoryItem[] | undefined
  historyLoading?: boolean | undefined
  children?: React.ReactNode
}

export const CourierDetailContent: React.FC<CourierDetailContentProps> = ({
  courier,
  history,
  historyLoading = false,
  children
}) => {
  return (
    <div className="space-y-6">
      {/* Причина отклонения и поля к исправлению */}
      {(courier.moderation_comment || (courier.moderation_fields && courier.moderation_fields.length > 0)) && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-5">
          <h4 className="text-base font-medium text-red-800 mb-3 flex items-center">
            <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
            Причина отклонения
          </h4>
          {courier.moderation_comment && (
            <p className="text-sm text-red-700 whitespace-pre-wrap mb-3">
              {courier.moderation_comment}
            </p>
          )}
          {courier.moderation_fields && courier.moderation_fields.length > 0 && (
            <div>
              <p className="text-sm font-medium text-red-800 mb-2">
                Поля к исправлению:
              </p>
              <div className="flex flex-wrap gap-2">
                {courier.moderation_fields.map((key) => (
                  <span
                    key={key}
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200"
                  >
                    {FIELD_OPTIONS.find((field) => field.key === key)?.label || key}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* История решений */}
      <div className="mt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">История решений</h4>
        {historyLoading ? (
          <p className="text-sm text-gray-500">Загрузка…</p>
        ) : history && history.length > 0 ? (
          <ul className="space-y-2">
            {history.map((ev, i) => (
              <li key={i} className="text-sm border-l-2 border-gray-200 pl-3">
                <div className="text-gray-500">{fmt(ev.created_at)}</div>
                <div className="text-gray-900">
                  {ACTION_LABELS[ev.action] ?? ev.action}
                  {ev.new_status !== undefined && ev.new_status !== null
                    ? ` → ${STATUS_LABELS[ev.new_status] ?? ev.new_status}` : ''}
                </div>
                {ev.admin_name && <div className="text-gray-500">Админ: {ev.admin_name}</div>}
                {ev.reason && <div className="text-red-600">Причина: {ev.reason}</div>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500">Нет событий</p>
        )}
      </div>

      {children}

      {/* Personal Information with Profile Photo */}
      <Card>
        <div className="p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <UserIcon className="w-5 h-5 mr-2" />
            Личные данные
          </h4>
          <div className="flex gap-6">
            {/* Profile Photo */}
            <div className="flex-shrink-0">
              <div className="w-20 h-20">
                <ImageViewer
                  src={courier.profile_image_base64}
                  alt="Фото профиля"
                  placeholder="Нет фото"
                />
              </div>
            </div>
            {/* Personal Data */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Фамилия
                </label>
                <p className="text-gray-900">{courier.last_name || '—'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Имя
                </label>
                <p className="text-gray-900">{courier.first_name || '—'}</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Отчество
                </label>
                <p className="text-gray-900">{courier.middle_name || '—'}</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Contact Information */}
      <Card>
        <div className="p-5">
          <h4 className="text-base font-medium text-gray-900 mb-4 flex items-center">
            <EnvelopeIcon className="w-4 h-4 mr-2" />
            Контактная информация
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Телефон
              </label>
              <p className="text-gray-900 text-sm flex items-center">
                <PhoneIcon className="w-4 h-4 mr-2 text-gray-400" />
                {courier.phone || '—'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <p className="text-gray-900 text-sm flex items-center">
                <EnvelopeIcon className="w-4 h-4 mr-2 text-gray-400" />
                {courier.email || '—'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Регион
              </label>
              <p className="text-gray-900 text-sm flex items-center">
                <MapPinIcon className="w-4 h-4 mr-2 text-gray-400" />
                {courier.region_name || '—'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Адрес
              </label>
              <p className="text-gray-900 text-sm flex items-center">
                <HomeIcon className="w-4 h-4 mr-2 text-gray-400" />
                {courier.address || '—'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Координаты
              </label>
              <p className="text-gray-900 text-sm flex items-center">
                <GlobeAltIcon className="w-4 h-4 mr-2 text-gray-400" />
                {courier.latitude && courier.longitude
                  ? `${courier.latitude}, ${courier.longitude}`
                  : '—'
                }
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Work Information */}
      <ModerationSection
        title="Рабочая информация"
        icon={<BriefcaseIcon className="w-4 h-4" />}
        status={(courier.transport_types_status as 'approved' | 'pending' | 'rejected') || 'approved'}
        hasModeration={!!(courier.transport_types_pending && courier.transport_types_pending.length > 0)}
      >
          <div className="space-y-6">
            {/* Виды деятельности */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Виды деятельности
              </label>
              {courier.activity_types && courier.activity_types.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {courier.activity_types.map((activity) => (
                    <span
                      key={activity.activity_type_id}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      <BriefcaseIcon className="w-3 h-3 mr-1" />
                      {activity.name}
                      {activity.allows_multiselect && (
                        <span className="ml-1 text-gray-600" title="Множественный выбор разрешен">●</span>
                      )}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-gray-900">{courier.activity_type_name || '—'}</p>
              )}
            </div>

            {/* Комментарий к виду деятельности «Прочее» */}
            {courier.other_activity_comment?.trim() && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Что именно вы перевозите?
                </label>
                <p className="text-gray-900 whitespace-pre-wrap">
                  {courier.other_activity_comment.trim()}
                </p>
              </div>
            )}

            {/* Виды транспорта с модерацией */}
            <ModerationTransportTypes
              currentTypes={courier.transport_types_current ?? []}
              pendingTypes={courier.transport_types_pending ?? []}
              status={(courier.transport_types_status as 'approved' | 'pending' | 'rejected') || 'approved'}
              label="Виды транспорта"
            />

            {/* Доставка */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Радиус доставки
                </label>
                <p className="text-gray-900 flex items-center">
                  <GlobeAltIcon className="w-4 h-4 mr-2 text-gray-400" />
                  {courier.delivery_radius ? `${courier.delivery_radius} км` : '—'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Широта
                </label>
                <p className="text-gray-900 flex items-center">
                  <MapPinIcon className="w-4 h-4 mr-2 text-gray-400" />
                  {courier.latitude || '—'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Долгота
                </label>
                <p className="text-gray-900 flex items-center">
                  <MapPinIcon className="w-4 h-4 mr-2 text-gray-400" />
                  {courier.longitude || '—'}
                </p>
              </div>
            </div>
          </div>
      </ModerationSection>

      {/* Passport Information */}
      <Card>
        <div className="p-5">
          <h4 className="text-base font-medium text-gray-900 mb-4 flex items-center">
            <IdentificationIcon className="w-4 h-4 mr-2" />
            Паспортные данные
            {/* Сводка по паспорту */}
            <div className="ml-auto text-sm">
              {(() => {
                const passportDocs = [
                  courier.passport_image_first_base64,
                  courier.passport_image_registration_base64
                ];
                const uploaded = passportDocs.filter(Boolean).length;
                const total = passportDocs.length;

                if (uploaded === total) {
                  return (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      ✓ Полный комплект ({uploaded}/{total})
                    </span>
                  );
                } else if (uploaded > 0) {
                  return (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      ⚠ Частично загружено ({uploaded}/{total})
                    </span>
                  );
                } else {
                  return (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      ✗ Не загружено (0/{total})
                    </span>
                  );
                }
              })()}
            </div>
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ИНН
              </label>
              <p className="text-gray-900">{courier.inn || '—'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Серия и номер паспорта
              </label>
              <p className="text-gray-900">
                {courier.passport || '—'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Серия и номер водительского удостоверения
              </label>
              <p className="text-gray-900">
                {courier.driver_license || '—'}
              </p>
            </div>
          </div>

          {/* Passport Images */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Первый разворот
              </label>
              <div className="w-48 h-32">
                <ImageViewer
                  src={courier.passport_image_first_base64}
                  alt="Первый разворот паспорта"
                  placeholder="Нет фото"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Разворот с регистрацией
              </label>
              <div className="w-48 h-32">
                <ImageViewer
                  src={courier.passport_image_registration_base64}
                  alt="Разворот с регистрацией"
                  placeholder="Нет фото"
                />
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Driver License Information */}
      <ModerationSection
        title="Водительское удостоверение"
        icon={<DocumentIcon className="w-4 h-4" />}
        status={(courier.driver_license_status as 'approved' | 'pending' | 'rejected') || 'approved'}
        hasModeration={!!(courier.driver_license_pending || courier.driver_license_image_pending_base64)}
      >

          <div className="space-y-4">
            {/* Серия и номер водительского удостоверения с модерацией */}
            <ModerationStatus
              status={(courier.driver_license_status as 'approved' | 'pending' | 'rejected') || 'approved'}
              currentValue={courier.driver_license_current ?? null}
              pendingValue={courier.driver_license_pending ?? null}
              label="Серия и номер водительского удостоверения"
            />

            {/* Изображение водительского удостоверения с модерацией */}
            <ModerationImageViewer
              currentImage={courier.driver_license_image_current_base64 ?? null}
              pendingImage={courier.driver_license_image_pending_base64 ?? null}
              status={(courier.driver_license_status as 'approved' | 'pending' | 'rejected') || 'approved'}
              alt="Водительское удостоверение"
              placeholder="Нет фото водительского удостоверения"
              label="Фото водительского удостоверения"
            />
          </div>
      </ModerationSection>

      {/* System Information */}
      <Card>
        <div className="p-5">
          <h4 className="text-base font-medium text-gray-900 mb-4 flex items-center">
            <ClockIcon className="w-4 h-4 mr-2" />
            Системная информация
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Дата регистрации
              </label>
              <p className="text-gray-900">{courier.created_at_formatted}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Последнее обновление
              </label>
              <p className="text-gray-900">{courier.updated_at_formatted}</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}
